import Env from "@/config/environment";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useGlobalState } from "@/features/utils/use-global-state";
import type { Voice } from "@/features/voices/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { VoicesAPI } from "../api/voices";
import { voiceKeys } from "./use-voices";

export const sampleUrl = (path?: string) =>
  path
    ? `${Env.server}/api/files/v1/download?f=${encodeURIComponent(path)}`
    : undefined;

/**
 * Whether a voice is heard through its per-temperament preview rather than its one stored
 * sample: an ElevenLabs voice, served by the expressive model (backend
 * `VoiceService.getTemperamentSample`).
 */
export const hasTemperamentSample = (voice?: Voice) =>
  voice?.provider_name === "elevenlabs";

export interface SampleOptions {
  temperament?: string;
  language?: string | null;
}

/**
 * One sample at a time, whichever row or button asked for it.
 *
 * A single element rather than one per row: creating an `Audio` per row would start
 * downloading every sample the moment the list opens. It takes the same global "currently
 * playing" slot as `useAudioPlayer`, so a preview started here stops any other player on
 * the page, and the other way round.
 *
 * An ElevenLabs voice plays its preview for the temperament and language asked for, which
 * the server synthesizes the first time anybody asks and keeps afterwards — so the first
 * click may take a couple of seconds (`loadingId`), and the key it returns is remembered
 * for the rest of the session. Any other voice, or a preview that fails, plays the voice's
 * stored sample.
 */
export function useSamplePlayer() {
  const slot = useId();
  const queryClient = useQueryClient();
  const clientId = useCurrentClient()?.client?.id || "";
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // The last request, so a second click during a slow first one wins over it.
  const requestRef = useRef(0);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [current, setCurrent] = useGlobalState<string | null>(
    "audio-player-current",
    null,
  );

  const halt = () => {
    requestRef.current++;
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
    setLoadingId(null);
  };

  /** Stops what this player is playing and gives the slot back. */
  const stop = () => {
    if (audioRef.current) setCurrent(null);
    halt();
  };

  // Somebody else took the slot: yield it.
  useEffect(() => {
    if (playingId && current !== `${slot}-${playingId}`) halt();
  }, [current]);

  useEffect(() => stop, []);

  const resolve = async (voice: Voice, options: SampleOptions) => {
    if (!hasTemperamentSample(voice) || !clientId) return voice.sample;
    const temperament = options.temperament || "professional";
    const language = options.language || voice.language || "";
    try {
      const { sample } = await queryClient.fetchQuery({
        queryKey: voiceKeys.sample(clientId, voice.id, temperament, language),
        queryFn: () =>
          VoicesAPI.sample(clientId, voice.id, temperament, language),
        staleTime: Infinity,
      });
      return sample || voice.sample;
    } catch {
      return voice.sample;
    }
  };

  const toggle = async (voice: Voice, options: SampleOptions = {}) => {
    if (playingId === voice.id || loadingId === voice.id) {
      stop();
      return;
    }
    halt();
    const request = requestRef.current;
    setLoadingId(voice.id);
    const url = sampleUrl(await resolve(voice, options));
    if (request !== requestRef.current) return;
    setLoadingId(null);
    if (!url) return;

    const audio = new Audio(url);
    audio.onended = () => stop();
    audioRef.current = audio;
    setPlayingId(voice.id);
    setCurrent(`${slot}-${voice.id}`);
    audio.play().catch(() => {
      if (audioRef.current === audio) stop();
    });
  };

  return { playingId, loadingId, toggle, stop };
}
